import { Box } from '@mantine/core';
import type { UserOut as IUser } from '@repo/api-codegen/v2';
import SingleSelector from '@repo/common/components/SingleSelector/SingleSelector';
import type { FormikProps } from 'formik';
import { noop } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useAuthUser } from '../../../api/hooks/authUser';
import {
	useExtendedUserList,
	useGuestUserList,
} from '../../../api/hooks/user/useExtendedUserList';
import type { DjangoValueType } from '../../../pages/TemplatePage/types';
import type { SidebarEntityKeys } from '../../EntityPageLayout/types';
import { getAssigneeAndGroupSelectorOptions } from '../../EntityPageLayout/utils';
import type { TemplateFormik } from './ManageTemplatePage';

export interface AssigneePropertySelectorProps {
	formik: FormikProps<TemplateFormik>;
}

export function AssigneeTemplateSelector({
	formik,
}: AssigneePropertySelectorProps) {
	const { isGuestUser } = useAuthUser();
	const { usersVisibleToGuests } = useGuestUserList();
	const { activeUsers, userGroups } = useExtendedUserList();

	const handleEntityChange = useCallback(
		(key: SidebarEntityKeys) => (value: DjangoValueType) => {
			if (key === 'assigned_to')
				formik.setFieldValue('assigned_to', value?.toString() ?? '');
			if (key === 'assigned_to_group')
				formik.setFieldValue('assigned_to_group', value?.toString() ?? '');
		},
		[formik]
	);

	const visibleUsers = (isGuestUser
		? usersVisibleToGuests
		: activeUsers) as unknown as IUser[];

	const assignees = useMemo(
		() =>
			getAssigneeAndGroupSelectorOptions(
				visibleUsers,
				userGroups ?? [],
				handleEntityChange
			),
		[visibleUsers, userGroups, handleEntityChange]
	);

	return (
		<Box maw={256}>
			<SingleSelector
				placeholder="Add assignee"
				placeholderIconName="user"
				variant={
					formik.values.assigned_to || formik.values.assigned_to_group
						? 'default'
						: 'tertiary'
				}
				iconType="avatar"
				isViewerUser={false}
				options={assignees}
				initialSelected={
					formik.values.assigned_to || formik.values.assigned_to_group
				}
				// SingleSelector require onChange != null to allow changes
				// Actual change logic is handled by options.onClick
				onChange={noop}
				property="owner"
				searchable
			/>
		</Box>
	);
}
